// Phase 8 — Workspace switcher dropdown for the top nav.
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Check, ChevronDown, Plus, Settings } from "lucide-react";
import { useWorkspace } from "../lib/WorkspaceContext";
import { useAuth } from "../lib/AuthContext";

export default function WorkspaceSwitcher() {
    const { workspaces, active, activeId, setActiveId, refresh } = useWorkspace();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        function onClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    if (!user) return null;

    const label = active ? active.name : "Workspace";

    return (
        <div className="relative" ref={ref} data-testid="workspace-switcher">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="workspace-switcher-trigger"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink/15 hover:border-ink/40 text-[12px] font-mono uppercase tracking-[0.14em] text-ink transition-colors"
            >
                <Building2 size={12} className="text-ink-soft" />
                <span className="max-w-[180px] truncate">{label}</span>
                {active?.role && (
                    <span className="text-amber-accent normal-case font-mono text-[10px]">· {active.role}</span>
                )}
                <ChevronDown size={11} className="text-ink-soft" />
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 w-72 bg-paper border border-ink/15 shadow-lg z-50" data-testid="workspace-switcher-menu">
                    <div className="max-h-72 overflow-y-auto">
                        {workspaces.length === 0 && (
                            <div className="px-3 py-4 text-[12px] text-ink-soft italic">No workspaces.</div>
                        )}
                        {workspaces.map((w) => (
                            <button
                                key={w.id}
                                onClick={() => { setActiveId(w.id); setOpen(false); }}
                                data-testid={`workspace-option-${w.id.slice(0,8)}`}
                                className={`w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-paper-soft border-l-2 ${w.id === activeId ? "border-amber-accent" : "border-transparent"}`}
                            >
                                <span className="flex-1 min-w-0">
                                    <span className="block text-[13px] text-ink truncate">{w.name}</span>
                                    <span className="block font-mono text-[9px] uppercase tracking-[0.16em] text-ink-soft mt-0.5">
                                        {w.role} · {w.member_count} member{w.member_count === 1 ? "" : "s"}{w.is_system ? " · public" : ""}
                                    </span>
                                </span>
                                {w.id === activeId && <Check size={13} className="text-amber-accent mt-0.5" />}
                            </button>
                        ))}
                    </div>
                    <div className="border-t border-ink/12">
                        <Link
                            to="/workspaces"
                            onClick={() => setOpen(false)}
                            data-testid="workspace-switcher-manage"
                            className="flex items-center gap-2 px-3 py-2 text-[12px] text-ink hover:bg-paper-soft"
                        >
                            <Settings size={12} />
                            Manage workspaces
                        </Link>
                        <button
                            onClick={() => { setOpen(false); navigate("/workspaces?create=1"); refresh(); }}
                            data-testid="workspace-switcher-create"
                            className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-amber-accent hover:bg-paper-soft border-t border-ink/8"
                        >
                            <Plus size={12} />
                            New workspace
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
